import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { DateInputIT } from "@/components/ui/date-input-it";
import { errMsg } from "@/lib/errors";
import {
  Scale, ExternalLink, Search, Plus, Pencil, Trash2, Loader2, X,
  AlertTriangle, BookOpen, FileText, ChevronDown, RefreshCw, BadgeCheck,
  Paperclip, Download,
} from "lucide-react";

const TYPE_META = {
  nazionale: { label: "Nazionale", color: "bg-blue-100 text-blue-800 border-blue-300", flag: "🇮🇹" },
  regionale: { label: "Regionale (Calabria)", color: "bg-emerald-100 text-emerald-800 border-emerald-300", flag: "🌄" },
  comunale: { label: "Comunale (Reggio C.)", color: "bg-amber-100 text-amber-800 border-amber-300", flag: "🏛" },
  provinciale: { label: "Provinciale", color: "bg-orange-100 text-orange-800 border-orange-300", flag: "📍" },
  eu: { label: "Europea", color: "bg-violet-100 text-violet-800 border-violet-300", flag: "🇪🇺" },
};

const RegulationsTab = () => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState(new Set());
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [alertsCount, setAlertsCount] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType !== "all") params.type = filterType;
      if (q) params.q = q;
      const [r, a] = await Promise.all([
        api.get("/admin/regulations", { params }),
        api.get("/admin/regulation-alerts").catch(() => ({ data: { items: [], new_count: 0 } })),
      ]);
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
      setNewCount(r.data?.new_count || 0);
      setAlerts(a.data?.items || []);
      setAlertsCount(a.data?.new_count || 0);
    } catch (e) {
      toast.error(errMsg(e, "Errore caricamento"));
    } finally { setLoading(false); }
  }, [filterType, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const toggleExpand = (id) => setExpanded((s) => {
    const ns = new Set(s);
    if (ns.has(id)) ns.delete(id); else ns.add(id);
    return ns;
  });

  const seedDefaults = async () => {
    if (!window.confirm("Ricarica/aggiorna le normative dal seed ufficiale? Le tue note manuali NON verranno cancellate.")) return;
    try {
      const r = await api.post("/admin/regulations/seed");
      toast.success(`Seed completato · ${r.data.total} norme totali`);
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore seed"));
    }
  };

  const onDelete = async (reg) => {
    if (!window.confirm(`Eliminare "${reg.title}"?`)) return;
    try {
      await api.delete(`/admin/regulations/${reg.id}`);
      toast.success("Eliminata");
      load();
    } catch (e) {
      toast.error(errMsg(e, "Errore"));
    }
  };

  const markSeen = async (reg) => {
    try {
      await api.patch(`/admin/regulations/${reg.id}`, { is_new: false, is_updated: false });
      load();
    } catch { /* ignore */ }
  };

  const filteredCount = items.length;

  return (
    <div className="space-y-4" data-testid="regulations-tab">
      {/* Header */}
      <div className="bg-white border border-[#E8E2D9] rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-display text-2xl text-[#1E3A5F] flex items-center gap-2">
              <Scale className="w-6 h-6 text-[#AC5D45]" /> Normativa & Regolamenti
              {newCount > 0 && (
                <Badge className="bg-rose-600 text-white animate-pulse">
                  {newCount} novità
                </Badge>
              )}
            </h2>
            <p className="text-sm text-[#635F59] mt-1">
              Knowledge base sempre aggiornata — leggi nazionali, regionali (Calabria) e comunali (Reggio Calabria) per B&amp;B family.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={async () => {
              try {
                const r = await api.post("/admin/regulations/poll-now");
                toast.success(`Polling ok · ${r.data.alerts_new} nuove segnalazioni`);
                load();
              } catch (e) {
                toast.error(errMsg(e, "Errore polling"));
              }
            }} data-testid="reg-poll-btn">
              <Search className="w-4 h-4 mr-1" /> Cerca aggiornamenti ora
            </Button>
            <Button variant="outline" size="sm" onClick={seedDefaults} data-testid="reg-seed-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Aggiorna seed ufficiale
            </Button>
            <Button className="bg-[#AC5D45] hover:bg-[#8B4A37] text-white" size="sm" onClick={() => { setEditing(null); setShowEditor(true); }} data-testid="reg-new-btn">
              <Plus className="w-4 h-4 mr-1" /> Nuova norma manuale
            </Button>
          </div>
        </div>
      </div>

      {/* Alerts banner */}
      {alertsCount > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-rose-900">{alertsCount} potenziali nuove normative trovate</h3>
              <p className="text-xs text-rose-700">Le verifiche automatiche settimanali hanno individuato fonti potenzialmente rilevanti. Controllale qui sotto.</p>
              <div className="mt-2 space-y-1">
                {alerts.slice(0, 5).map((a) => (
                  <div key={a.id} className="text-xs bg-white border border-rose-200 rounded p-2 flex items-start gap-2">
                    <span className="font-mono text-rose-700">{(a.found_at || "").slice(0, 10)}</span>
                    <a href={a.source_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex-1 truncate">
                      {a.title}
                    </a>
                    <button onClick={async () => { await api.post(`/admin/regulation-alerts/${a.id}/promote`); toast.success("Promosso a normativa"); load(); }} className="text-emerald-700 underline" title="Aggiungi a Knowledge Base">
                      Aggiungi
                    </button>
                    <button onClick={async () => { await api.post(`/admin/regulation-alerts/${a.id}/dismiss`); load(); }} className="text-[#9b948a] hover:text-rose-600">
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-[#FAF7F2] border border-[#E8E2D9] rounded-lg p-3 flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#9b948a]" />
          <Input className="pl-7" placeholder="Cerca titolo, ente, riassunto, tag…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="reg-search" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-[200px]" data-testid="reg-type-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte ({items.length})</SelectItem>
            {Object.entries(TYPE_META).map(([k, m]) => (
              <SelectItem key={k} value={k}>{m.flag} {m.label} {counts[k] ? `(${counts[k]})` : ""}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(q || filterType !== "all") && (
          <Button variant="outline" size="sm" onClick={() => { setQ(""); setFilterType("all"); }}>
            <X className="w-3.5 h-3.5 mr-1" /> Pulisci
          </Button>
        )}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-12 text-[#635F59]">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Caricamento…
        </div>
      ) : filteredCount === 0 ? (
        <div className="text-center py-12 text-[#9b948a] italic">
          Nessuna normativa trovata. Premi <b>Aggiorna seed ufficiale</b> per caricare il database iniziale.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => {
            const meta = TYPE_META[r.type] || TYPE_META.nazionale;
            const isExpanded = expanded.has(r.id);
            return (
              <div
                key={r.id}
                className={`bg-white border rounded-xl overflow-hidden transition-colors ${r.is_new ? "border-rose-400 ring-2 ring-rose-100" : "border-[#E8E2D9]"}`}
                data-testid={`reg-item-${r.id}`}
              >
                <button
                  type="button"
                  className="w-full text-left p-4 hover:bg-[#FAF7F2] transition-colors"
                  onClick={() => toggleExpand(r.id)}
                  data-testid={`reg-toggle-${r.id}`}
                >
                  <div className="flex items-start gap-3">
                    <ChevronDown className={`w-4 h-4 text-[#635F59] mt-1 shrink-0 transition-transform ${isExpanded ? "" : "-rotate-90"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded border ${meta.color}`}>
                          {meta.flag} {meta.label}
                        </span>
                        {r.is_new && (
                          <span className="text-[10px] font-bold bg-rose-600 text-white px-1.5 py-0.5 rounded animate-pulse">🆕 NUOVA</span>
                        )}
                        {r.is_updated && (
                          <span className="text-[10px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded">⚠️ AGGIORNATA</span>
                        )}
                        {r.is_manual && (
                          <span className="text-[10px] bg-[#E8E2D9] text-[#635F59] px-1.5 py-0.5 rounded">manuale</span>
                        )}
                      </div>
                      <h3 className="font-semibold text-[#1E3A5F] mt-1.5 leading-snug">{r.title}</h3>
                      <p className="text-xs text-[#635F59] mt-0.5">
                        {r.ente} · pubblicata {r.data_pubblicazione || "—"}
                        {r.data_aggiornamento && r.data_aggiornamento !== r.data_pubblicazione && ` · aggiornata ${r.data_aggiornamento}`}
                      </p>
                    </div>
                  </div>
                </button>
                {isExpanded && (
                  <div className="border-t border-[#E8E2D9] p-4 bg-[#FDFBF7] space-y-3">
                    {r.summary && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#AC5D45] font-bold">Riassunto</Label>
                        <p className="text-sm text-[#2D2926] mt-1 leading-relaxed">{r.summary}</p>
                      </div>
                    )}
                    {r.articoli_rilevanti && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#AC5D45] font-bold">Articoli rilevanti</Label>
                        <pre className="text-xs text-[#2D2926] mt-1 leading-relaxed whitespace-pre-wrap font-sans">{r.articoli_rilevanti}</pre>
                      </div>
                    )}
                    {r.scadenze_ricorrenti?.length > 0 && (
                      <div>
                        <Label className="text-[10px] uppercase tracking-wider text-[#AC5D45] font-bold">Scadenze ricorrenti</Label>
                        <ul className="text-xs text-[#2D2926] mt-1 space-y-0.5 list-disc list-inside">
                          {r.scadenze_ricorrenti.map((s, i) => (
                            <li key={`${r.id}-scad-${s.label}-${i}`}><b>{s.label}</b> · {s.frequency}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {r.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {r.tags.map((tag) => (
                          <span key={tag} className="text-[10px] bg-[#E8E2D9] text-[#635F59] px-1.5 py-0.5 rounded">#{tag}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center gap-2 pt-2 border-t border-[#E8E2D9] flex-wrap">
                      {r.source_url && (
                        <a
                          href={r.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs bg-[#1E3A5F] hover:bg-[#142640] text-white px-2.5 py-1.5 rounded font-medium"
                          data-testid={`reg-source-${r.id}`}
                        >
                          <ExternalLink className="w-3 h-3" /> Fonte ufficiale
                        </a>
                      )}
                      {(r.additional_links || []).map((lnk, idx) => (
                        lnk?.url ? (
                          <a
                            key={`${r.id}-link-${lnk.url}-${idx}`}
                            href={lnk.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs bg-[#4A6B8F] hover:bg-[#3A5675] text-white px-2.5 py-1.5 rounded font-medium"
                            data-testid={`reg-link-${r.id}-${idx}`}
                            title={lnk.url}
                          >
                            <ExternalLink className="w-3 h-3" /> {lnk.label || `Link ${idx + 2}`}
                          </a>
                        ) : null
                      ))}
                      {/* Allegati: nuovo array + fallback al legacy attachment_url */}
                      {(Array.isArray(r.attachments) && r.attachments.length
                        ? r.attachments
                        : (r.attachment_url ? [{ url: r.attachment_url, name: r.attachment_name }] : [])
                      ).map((att, idx) => (
                        <a
                          key={att.url}
                          href={att.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1.5 rounded font-medium max-w-[220px]"
                          data-testid={`reg-attachment-${r.id}-${idx}`}
                          title={att.name || "Documento allegato"}
                        >
                          <Download className="w-3 h-3 shrink-0" />
                          <span className="truncate">{att.name || `Allegato ${idx + 1}`}</span>
                        </a>
                      ))}
                      {(r.is_new || r.is_updated) && (
                        <Button size="sm" variant="outline" onClick={() => markSeen(r)} className="text-xs">
                          <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Letto
                        </Button>
                      )}
                      <div className="ml-auto flex items-center gap-1">
                        <button onClick={() => { setEditing(r); setShowEditor(true); }} className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-white" title="Modifica">
                          <Pencil className="w-3.5 h-3.5 text-[#1E3A5F]" />
                        </button>
                        {r.is_manual && (
                          <button onClick={() => onDelete(r)} className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-rose-50" title="Elimina">
                            <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <RegulationEditor
        open={showEditor}
        onClose={() => setShowEditor(false)}
        onSaved={() => { setShowEditor(false); load(); }}
        editing={editing}
      />
    </div>
  );
};

const RegulationEditor = ({ open, onClose, onSaved, editing }) => {
  const [form, setForm] = useState({
    title: "", type: "nazionale", ente: "", source_url: "",
    additional_links: [],  // [{url, label}, …]
    summary: "",
    articoli_rilevanti: "", tags: "", data_pubblicazione: "", data_aggiornamento: "",
  });
  const [saving, setSaving] = useState(false);
  // Iter164: files staged for upload after save (multi-file support). We
  // wait to upload until the regulation record exists so we always have a
  // stable reg_id. Existing attachments come from the server (see effect).
  const [pendingFiles, setPendingFiles] = useState([]);        // File[]
  const [existingAttachments, setExistingAttachments] = useState([]);  // [{url,name}]

  useEffect(() => {
    if (open) {
      // Read either the new `attachments[]` array or fall back to the
      // legacy single `attachment_url` field on older records so nothing
      // disappears from the UI after the schema switch.
      const attachments = Array.isArray(editing?.attachments) && editing.attachments.length
        ? editing.attachments
        : (editing?.attachment_url
            ? [{ url: editing.attachment_url, name: editing.attachment_name || "Documento allegato" }]
            : []);
      setForm(editing ? {
        ...editing,
        tags: (editing.tags || []).join(", "),
        additional_links: Array.isArray(editing.additional_links) ? editing.additional_links : [],
      } : {
        title: "", type: "nazionale", ente: "", source_url: "",
        additional_links: [],
        summary: "", articoli_rilevanti: "", tags: "",
        data_pubblicazione: "", data_aggiornamento: "",
      });
      setPendingFiles([]);
      setExistingAttachments(attachments);
    }
  }, [open, editing]);

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Titolo obbligatorio");
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        additional_links: (form.additional_links || [])
          .filter((l) => l.url?.trim())
          .map((l) => ({ url: l.url.trim(), label: (l.label || "").trim() })),
      };
      let regId;
      if (editing) {
        await api.patch(`/admin/regulations/${editing.id}`, payload);
        regId = editing.id;
        toast.success("Aggiornata");
      } else {
        const r = await api.post("/admin/regulations", payload);
        regId = r.data?.id;
        toast.success("Creata");
      }
      // Upload every pending file. We upload them serially so the
      // toasts appear in the order the host picked them, and so a
      // network hiccup on file #3 doesn't hide the success of file #1.
      for (const f of pendingFiles) {
        try {
          const fd = new FormData();
          fd.append("file", f);
          await api.post(`/admin/regulations/${regId}/attachment`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success(`Allegato caricato: ${f.name}`);
        } catch (e) {
          toast.error(errMsg(e, `Upload di ${f.name} fallito`));
        }
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(errMsg(e, "Errore salvataggio"));
    } finally { setSaving(false); }
  };

  const removeExistingAttachment = async (att) => {
    if (!editing?.id) return;
    if (!window.confirm(`Rimuovere "${att.name || "allegato"}"?`)) return;
    try {
      await api.delete(`/admin/regulations/${editing.id}/attachment`, {
        params: { url: att.url },
      });
      setExistingAttachments((arr) => arr.filter((a) => a.url !== att.url));
      toast.success("Allegato rimosso");
    } catch (e) {
      toast.error(errMsg(e, "Errore rimozione allegato"));
    }
  };

  // Helpers for the additional_links + pendingFiles arrays
  const addLink = () => setForm((f) => ({
    ...f, additional_links: [...(f.additional_links || []), { url: "", label: "" }],
  }));
  const updateLink = (idx, field, val) => setForm((f) => {
    const arr = [...(f.additional_links || [])];
    arr[idx] = { ...arr[idx], [field]: val };
    return { ...f, additional_links: arr };
  });
  const removeLink = (idx) => setForm((f) => ({
    ...f, additional_links: (f.additional_links || []).filter((_, i) => i !== idx),
  }));
  const addPendingFiles = (fileList) => {
    if (!fileList || !fileList.length) return;
    setPendingFiles((prev) => [...prev, ...Array.from(fileList)]);
  };
  const removePendingFile = (idx) => setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="reg-editor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1E3A5F]">
            <BookOpen className="w-5 h-5 text-[#AC5D45]" /> {editing ? "Modifica normativa" : "Nuova normativa"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Titolo *</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="reg-form-title" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Tipo *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="reg-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_META).map(([k, m]) => (
                    <SelectItem key={k} value={k}>{m.flag} {m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Ente emittente</Label>
              <Input value={form.ente} onChange={(e) => setForm({ ...form, ente: e.target.value })} placeholder="Es. Ministero del Turismo" />
            </div>
          </div>
          <div>
            <Label className="text-xs">URL fonte ufficiale</Label>
            <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://…" />
          </div>

          {/* Iter164: multiple additional reference links (opzionali) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Altri link di riferimento</Label>
              <button
                type="button"
                onClick={addLink}
                className="inline-flex items-center gap-1 text-xs bg-[#1E3A5F] hover:bg-[#142640] text-white px-2 py-1 rounded"
                data-testid="reg-add-link"
              >
                <Plus className="w-3 h-3" /> Aggiungi link
              </button>
            </div>
            {(form.additional_links || []).length === 0 ? (
              <p className="text-[11px] text-[#635F59]">Nessun link aggiuntivo. Usa <strong>Aggiungi link</strong> per allegare approfondimenti, circolari collegate, guide, ecc.</p>
            ) : (
              <div className="space-y-2">
                {(form.additional_links || []).map((lnk, idx) => (
                  <div key={`link-edit-${idx}-${lnk.url || lnk.label || "new"}`} className="grid grid-cols-[110px_1fr_28px] gap-2 items-center">
                    <Input
                      value={lnk.label || ""}
                      onChange={(e) => updateLink(idx, "label", e.target.value)}
                      placeholder="Etichetta"
                      className="text-xs"
                      data-testid={`reg-link-label-${idx}`}
                    />
                    <Input
                      value={lnk.url || ""}
                      onChange={(e) => updateLink(idx, "url", e.target.value)}
                      placeholder="https://…"
                      className="text-xs"
                      data-testid={`reg-link-url-${idx}`}
                    />
                    <button
                      type="button"
                      onClick={() => removeLink(idx)}
                      className="w-7 h-7 inline-flex items-center justify-center rounded hover:bg-rose-50"
                      title="Rimuovi link"
                    >
                      <X className="w-4 h-4 text-rose-600" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Iter164: multiple attachments (PDF/DOC/JPG/PNG) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs flex items-center gap-1.5">
                <Paperclip className="w-3.5 h-3.5" />
                Documenti allegati
              </Label>
              <label className="inline-flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded cursor-pointer">
                <Plus className="w-3 h-3" /> Aggiungi PDF
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => { addPendingFiles(e.target.files); e.target.value = ""; }}
                  data-testid="reg-attachment-input"
                />
              </label>
            </div>
            {existingAttachments.length === 0 && pendingFiles.length === 0 && (
              <p className="text-[11px] text-[#635F59]">Nessun allegato. Usa <strong>Aggiungi PDF</strong> per caricare uno o più file (max 15 MB ciascuno).</p>
            )}
            {existingAttachments.length > 0 && (
              <div className="space-y-1.5">
                {existingAttachments.map((att) => (
                  <div key={att.url} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded px-3 py-1.5">
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-emerald-800 hover:underline min-w-0"
                    >
                      <FileText className="w-4 h-4 shrink-0" />
                      <span className="truncate">{att.name}</span>
                    </a>
                    <button
                      type="button"
                      onClick={() => removeExistingAttachment(att)}
                      className="text-xs text-rose-600 hover:underline flex items-center gap-1 shrink-0 ml-2"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Rimuovi
                    </button>
                  </div>
                ))}
              </div>
            )}
            {pendingFiles.length > 0 && (
              <div className="space-y-1 mt-2">
                <p className="text-[11px] font-medium text-[#635F59]">In coda per l'upload al salvataggio:</p>
                {pendingFiles.map((f, idx) => (
                  <div key={`${f.name}-${f.size ?? 0}-${f.lastModified ?? idx}`} className="flex items-center justify-between bg-[#FDFBF7] border border-dashed border-[#D4CFC5] rounded px-3 py-1.5">
                    <span className="text-sm text-[#635F59] truncate flex items-center gap-2 min-w-0">
                      <Paperclip className="w-3.5 h-3.5 shrink-0" /> {f.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => removePendingFile(idx)}
                      className="text-xs text-rose-600 hover:underline ml-2"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Riassunto</Label>
            <Textarea rows={3} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
          </div>
          <div>
            <Label className="text-xs">Articoli rilevanti / dettagli tecnici</Label>
            <Textarea rows={4} value={form.articoli_rilevanti} onChange={(e) => setForm({ ...form, articoli_rilevanti: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Data pubblicazione</Label>
              <DateInputIT value={form.data_pubblicazione || ""} onChange={(e) => setForm({ ...form, data_pubblicazione: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Data aggiornamento</Label>
              <DateInputIT value={form.data_aggiornamento || ""} onChange={(e) => setForm({ ...form, data_aggiornamento: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-xs">Tag (separati da virgola)</Label>
            <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="cin, obbligatorio, sanzione" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving} className="bg-[#AC5D45] hover:bg-[#8B4A37] text-white" data-testid="reg-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Salvo…</> : <><FileText className="w-4 h-4 mr-2" /> Salva</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RegulationsTab;
